import { supabase } from './supabaseClient'

export type IngredientNutritionRow = {
  canonical_name: string
  protein_100g: number | null
  carbs_100g: number | null
  fat_100g: number | null
  energy_kcal_100g: number | null
  fiber_100g: number | null
  sugar_100g: number | null
  sodium_100g: number | null
  primary_nutrient: string | null
  nutriscore_grade: string | null
  is_organic: boolean | null
  source: string | null
  off_product_name: string | null
  image_url: string | null
  data_completeness: string | null
}

const SELECT_FIELDS =
  'canonical_name, protein_100g, carbs_100g, fat_100g, energy_kcal_100g, fiber_100g, sugar_100g, sodium_100g, primary_nutrient, nutriscore_grade, is_organic, source, off_product_name, image_url, data_completeness'

export async function fetchIngredientNutrition(
  canonicalName: string,
): Promise<{ data: IngredientNutritionRow | null; error: string | null }> {
  const key = canonicalName.toLowerCase().trim()

  const { data: exact, error: exactError } = await supabase
    .from('ingredient_nutrition')
    .select(SELECT_FIELDS)
    .eq('canonical_name', key)
    .maybeSingle()

  if (exactError) return { data: null, error: exactError.message }
  if (exact) return { data: exact as IngredientNutritionRow, error: null }

  const { data: fuzzy, error: fuzzyError } = await supabase
    .from('ingredient_nutrition')
    .select(SELECT_FIELDS)
    .ilike('canonical_name', `%${key}%`)
    .limit(1)
    .maybeSingle()

  if (fuzzyError) return { data: null, error: fuzzyError.message }
  return { data: (fuzzy as IngredientNutritionRow | null) ?? null, error: null }
}

export function scaleNutritionPer100g(
  row: IngredientNutritionRow,
  grams: number,
): {
  energy_kcal: number
  protein_g: number
  carbs_g: number
  fat_g: number
  fiber_g: number
  sugar_g: number
  sodium_g: number
} {
  const ratio = grams / 100
  return {
    energy_kcal: Math.round(Number(row.energy_kcal_100g ?? 0) * ratio),
    protein_g: Math.round(Number(row.protein_100g ?? 0) * ratio * 10) / 10,
    carbs_g: Math.round(Number(row.carbs_100g ?? 0) * ratio * 10) / 10,
    fat_g: Math.round(Number(row.fat_100g ?? 0) * ratio * 10) / 10,
    fiber_g: Math.round(Number(row.fiber_100g ?? 0) * ratio * 10) / 10,
    sugar_g: Math.round(Number(row.sugar_100g ?? 0) * ratio * 10) / 10,
    sodium_g: Math.round(Number(row.sodium_100g ?? 0) * ratio * 10) / 10,
  }
}
